import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Loader,
  Menu,
  Skeleton,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconExternalLink, IconX } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { saveIdea } from "../../../../Api";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { buildIdeaUrlWithEvidenceFilters } from "../../../../utils/ideasUrlState";
import { useFields } from "../../../../hooks/useFields";
import { useKnowledgeData } from "../../../../hooks/useKnowledgeData";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import FiltersContext from "../../filters/context/FiltersContext";
import { SourceIcon } from "../../../../utils/SourceIcon";

const SAVE_TIMEOUT_MS = 30_000;

function readStatusValue(
  data: any,
  accessor: string,
  defaultVal?: string
): string {
  return (
    getNestedFieldValue(data, accessor) ??
    data?.status ??
    defaultVal ??
    "Unassigned"
  );
}

function isStatusPlaceholder(value: unknown): boolean {
  const t = String(value ?? "").trim();
  return t === "" || t === "-" || t === "—" || t === "–";
}

/** Shown in the cell when there is no real status (API often sends "-"). */
function formatStatusCellLabel(value: unknown): string {
  return isStatusPlaceholder(value) ? "Select Status" : String(value ?? "").trim();
}

export const StatusCell: React.FC<any> = ({
  data,
  col,
  renderParams = {},
}) => {
  const accessor = col?.accessor ?? "status";
  const auth0 = useAuth0();
  const { canEditIdeasInline } = useUserPermissions();
  // Theme rows (Bagel AI tab) have no real status/productItem.
  // Treat them as read-only and skip any productItem-driven UI to avoid
  // rendering broken links/icons against the stubbed object.
  const isThemeRow = !!data?._isThemeRow;
  const isReadOnly =
    !canEditIdeasInline || data?.state === "generated" || isThemeRow;

  const {
    ideaStatuses,
    markedDeliveryStatus,
    overwriteIdeaStatusWithLinkedDelivery,
  } = useFields();
  const { deliveryPlatform } = useKnowledgeData();
  const { activeFilters } = useContext(FiltersContext);

  const [status, setStatus] = useState(() =>
    readStatusValue(data, accessor, renderParams.default)
  );
  const [isSaving, setIsSaving] = useState(false);
  const saveSeqRef = useRef(0);

  useEffect(() => {
    setStatus(readStatusValue(data, accessor, renderParams.default));
  }, [data?._id, data?.status, accessor, renderParams.default]);

  const selectableStatuses = useMemo(() => {
    if (data?.linkedProductItemId) return ideaStatuses ?? [];
    return ideaStatuses?.filter((i: any) => !i?.isDefaultDelivery) ?? [];
  }, [data?.linkedProductItemId, ideaStatuses]);

  const productItem = isThemeRow ? null : data?.productItem;
  const deliveryStatusLive = productItem?.status ?? "Unassigned";
  const deliveryStatusStr =
    typeof deliveryStatusLive === "string"
      ? deliveryStatusLive
      : String(deliveryStatusLive ?? "");

  const selectedStatus = ideaStatuses?.find(
    (s: any) =>
      String(s.key ?? "").toLowerCase() === String(status ?? "").toLowerCase()
  );
  const isMarkedDeliveryStatus = markedDeliveryStatus?.find(
    (s: any) =>
      String(s.name ?? "").toLowerCase() ===
      String(selectedStatus?.key ?? "").toLowerCase()
  );

  const isStatusLinked =
    !!deliveryStatusStr &&
    !!markedDeliveryStatus?.some(
      (s: any) =>
        s.isStatusLink === true &&
        (String(s.name ?? "").toLowerCase() ===
          deliveryStatusStr.toLowerCase() ||
          s.id === deliveryStatusLive ||
          String(s.key ?? "").toLowerCase() === deliveryStatusStr.toLowerCase())
    );

  const showLinkedDeliveryInStatus =
    !!overwriteIdeaStatusWithLinkedDelivery && isStatusLinked;

  const menuDisabled = showLinkedDeliveryInStatus || isReadOnly;
  /** Table: always offer status options when editable (header hides menu for imported unless `enableEditOnImportedIdeas`). */
  const showStatusDropdown = !showLinkedDeliveryInStatus && !isReadOnly;

  const persistStatus = useCallback(
    async (itemKey: string): Promise<boolean> => {
      const seq = ++saveSeqRef.current;
      setIsSaving(true);
      try {
        const res = (await Promise.race([
          saveIdea(
            { id: data._id, status: itemKey, extraFields: true },
            auth0
          ),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("Status save timed out")),
              SAVE_TIMEOUT_MS
            )
          ),
        ])) as Awaited<ReturnType<typeof saveIdea>>;

        if (seq !== saveSeqRef.current) return false;

        if (res?.data?.success === false) {
          showNotification({
            title: "Error saving status",
            color: "red",
            message: "please try again...",
            icon: <IconX size="1.1rem" />,
            autoClose: 1200,
          });
          return false;
        }

        setStatus(itemKey);
        showNotification({
          title: "Status saved",
          color: "green",
          message: "Status saved successfully",
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });
        return true;
      } catch (e: any) {
        if (seq !== saveSeqRef.current) return false;
        console.error("Error saving status:", e);
        showNotification({
          title: "Error saving status",
          color: "red",
          message:
            e?.message === "Status save timed out"
              ? "Request timed out. Please try again."
              : "please try again...",
          icon: <IconX size="1.1rem" />,
          autoClose: 2200,
        });
        return false;
      } finally {
        if (seq === saveSeqRef.current) setIsSaving(false);
      }
    },
    [auth0, data._id]
  );

  const tooltipStatusLabel = showLinkedDeliveryInStatus
    ? deliveryStatusLive || productItem?.status || status
    : formatStatusCellLabel(status);
  const statusDisplayText = formatStatusCellLabel(status);
  const statusIsPlaceholder = isStatusPlaceholder(status);

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: "100%",
        maxWidth: "100%",
        textOverflow: "ellipsis",
        overflowX: "hidden",
        overflowY: "visible",
        position: "relative",
      }}
      className={`text-cell-${accessor.replace(/\./g, "-")}`}
    >
      <Group spacing={4} align="center" noWrap sx={{ width: "100%", minWidth: 0 }}>
        <Menu
          shadow="md"
          width={200}
          disabled={menuDisabled}
          withinPortal
          zIndex={10000}
        >
          <Menu.Target>
            <Tooltip
              label={
                <Box p={0.186}>
                  <Text fz={11.4} mb={1.4}>
                    Status:
                  </Text>
                  <Text
                    fz={11.4}
                    c={!showLinkedDeliveryInStatus && statusIsPlaceholder ? "dimmed" : undefined}
                    sx={
                      !showLinkedDeliveryInStatus && statusIsPlaceholder
                        ? { opacity: 0.72 }
                        : undefined
                    }
                  >
                    {tooltipStatusLabel}
                  </Text>
                </Box>
              }
              withinPortal
              openDelay={400}
            >
              {showLinkedDeliveryInStatus ? (
                <Box
                  component="a"
                  href={productItem?.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  h={24}
                  px={8}
                  py={4}
                  mx={"2px"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    borderRadius: "4px",
                    cursor: "pointer",
                    textDecoration: "none",
                    flex: "0 1 auto",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SourceIcon
                    sourceName={productItem?.origin || deliveryPlatform}
                    width={14}
                    height={14}
                  />
                  <Text
                    fz="12px"
                    lineClamp={1}
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      textAlign: "center",
                    }}
                    tt="capitalize"
                    miw={"65px"}
                  >
                    {deliveryStatusLive || productItem?.status || status}
                  </Text>
                </Box>
              ) : (
                <Flex
                  h={24}
                  px={8}
                  py={4}
                  mx={"2px"}
                  justify="center"
                  align="center"
                  sx={{
                    borderRadius: "4px",
                    cursor: menuDisabled ? "default" : "pointer",
                    flex: "0 1 auto",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  <Text
                    lineClamp={1}
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      textAlign: "center",
                      ...(statusIsPlaceholder
                        ? { opacity: 0.72, fontSize: "14px" }
                        : {}),
                    }}
                    tt="capitalize"
                    miw={"65px"}
                    c={statusIsPlaceholder ? "dimmed" : undefined}
                  >
                    {statusDisplayText}
                  </Text>
                  {isSaving && (
                    <Loader pl={4} size={14} color="#5C5CEB" />
                  )}
                </Flex>
              )}
            </Tooltip>
          </Menu.Target>

          {showStatusDropdown && (
            <Menu.Dropdown>
              <Menu.Label>Choose one...</Menu.Label>
              {(selectableStatuses?.length ? selectableStatuses : ideaStatuses ?? []).map(
                (item: any) => {
                  const allowPick =
                    !item.isDefaultDelivery &&
                    (!isMarkedDeliveryStatus?.isStatusLink || !data.linkedProductItemId);
                  const itemDisabled =
                    item.isDefaultDelivery === true &&
                    (!isMarkedDeliveryStatus?.isStatusLink || !data.linkedProductItemId);
                  return (
                    <Menu.Item
                      key={item.key}
                      disabled={itemDisabled}
                      onClick={
                        allowPick
                          ? async () => {
                            const oldValue = status;
                            const ok = await persistStatus(item.key);
                            if (!ok) setStatus(oldValue);
                          }
                          : undefined
                      }
                      tt="capitalize"
                      bg={item?.label === status ? "#f0f0f0" : "transparent"}
                    >
                      <Flex align="center" gap={8}>
                        <Text>{item?.label}</Text>
                      </Flex>
                    </Menu.Item>
                  );
                }
              )}
            </Menu.Dropdown>
          )}
        </Menu>
      </Group>

      <Tooltip label="Open idea in new tab" withArrow withinPortal>
        <ActionIcon
          className={`external-link-icon external-link-${accessor.replace(/\./g, "-")}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(
              buildIdeaUrlWithEvidenceFilters(
                data._id,
                window.location.search,
                activeFilters
              ),
              "_blank"
            );
          }}
          variant="transparent"
          size="sm"
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: "0px",
            zIndex: 10,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(2px)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
            "&:active": {
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
            "&:focus": {
              transform: "translateY(-50%)",
            },
          }}
        >
          <IconExternalLink size={18} color="#5b60e3" />
        </ActionIcon>
      </Tooltip>
    </Box>
  );
};

export const StatusCellSkeleton: React.FC<{ col: any; id?: string }> = ({
  col,
  id,
}) => {
  const renderParams = col.renderParams || {};
  const maxWidth = renderParams.maxWidth || col.maxWidth || "350px";
  const columnWidth = col.width;
  const minWidth = col.minWidth;

  const skeletonWidth = useMemo(() => {
    const seedString = id || col.accessor || "status";
    const hash = seedString.split("").reduce((acc: number, char: string) => {
      return acc + char.charCodeAt(0);
    }, 0);
    const percentage = 40 + (hash % 51);
    return `${percentage}%`;
  }, [col.accessor, id]);

  return (
    <Box
      sx={{
        maxWidth,
        width: columnWidth || "100%",
        minWidth,
        position: "relative",
        paddingRight: "23px",
      }}
    >
      <Flex
        h={24}
        px={8}
        py={4}
        mx="2px"
        justify="center"
        align="center"
        sx={{
          borderRadius: "4px",
          maxWidth: "calc(100% - 8px)",
        }}
      >
        <Skeleton height={12} width={skeletonWidth} radius="sm" />
      </Flex>
    </Box>
  );
};
